import { highlighterLogger } from './utils.js';

/**
 * Checks if a point is inside a bounding box
 * 
 * @param point The point to check
 * @param box The bounding box
 * @returns boolean indicating if the point is inside the box
 */
export function isPointInsideBox(point, box) {
  return point.x >= box.x &&
        point.x <= box.x + box.width &&
        point.y >= box.y &&
        point.y <= box.y + box.height;
}

/**
 * Calculates the overlap area between two bounding boxes
 * 
 * @param box1 First bounding box
 * @param box2 Second bounding box
 * @returns The overlap area
 */
export function calculateOverlap(box1, box2) {
  const xOverlap = Math.max(0,
    Math.min(box1.x + box1.width, box2.x + box2.width) -
    Math.max(box1.x, box2.x)
  );
  const yOverlap = Math.max(0,
    Math.min(box1.y + box1.height, box2.y + box2.height) -
    Math.max(box1.y, box2.y)
  );
  return xOverlap * yOverlap;
}

/**
 * Finds an exact match between candidate elements and the actual interaction element
 * 
 * @param candidate_elements Array of candidate element infos
 * @param actualInteractionElementInfo The actual interaction element info
 * @returns The matching candidate element info, or null if no match is found
 */
export function findExactMatch(candidate_elements, actualInteractionElementInfo) {
  if (!actualInteractionElementInfo.element) {
    return null;
  }

  const exactMatch = candidate_elements.find(elementInfo => 
    elementInfo.element && elementInfo.element === actualInteractionElementInfo.element
  );
  
  if (exactMatch) {
    highlighterLogger.debug('✅ Found exact element match:', {
      matchedElement: exactMatch.element?.tagName,
      matchedElementClass: exactMatch.element?.className,
      index: exactMatch.index
    });
    return exactMatch;
  }
  
  return null;
}

/**
 * Finds a match by traversing up the parent elements
 * 
 * @param candidate_elements Array of candidate element infos
 * @param actualInteractionElementInfo The actual interaction element info
 * @returns The matching candidate element info, or null if no match is found
 */
export function findParentMatch(candidate_elements, actualInteractionElementInfo) {
  if (!actualInteractionElementInfo.element) {
    return null;
  }

  let element = actualInteractionElementInfo.element;
  while (element.parentElement) {
    element = element.parentElement;
    const parentMatch = candidate_elements.find(candidate => 
      candidate.element && candidate.element === element
    );
    
    if (parentMatch) {
      highlighterLogger.debug('✅ Found parent element match:', {
        matchedElement: parentMatch.element?.tagName,
        matchedElementClass: parentMatch.element?.className,
        index: parentMatch.index,
        depth: element.tagName
      });
      return parentMatch;
    }
    
    // Stop if we hit another candidate element
    if (candidate_elements.some(candidate => 
      candidate.element && candidate.element === element
    )) {
      highlighterLogger.debug('⚠️ Stopped parent search - hit another candidate element:', element.tagName);
      break;
    }
  }
  
  return null;
}

/**
 * Finds a match based on spatial relationships between elements
 * 
 * @param candidate_elements Array of candidate element infos
 * @param actualInteractionElementInfo The actual interaction element info
 * @returns The matching candidate element info, or null if no match is found
 */
export function findSpatialMatch(candidate_elements, actualInteractionElementInfo) {
  if (!actualInteractionElementInfo.element || !actualInteractionElementInfo.bounding_box) {
    return null;
  }

  const actualBox = actualInteractionElementInfo.bounding_box;
  let bestMatch = null;
  let bestScore = 0;

  for (const candidateInfo of candidate_elements) {
    if (!candidateInfo.bounding_box) continue;
    
    const candidateBox = candidateInfo.bounding_box;
    let score = 0;

    // Check if actual element is contained within candidate
    if (isPointInsideBox({ x: actualBox.x, y: actualBox.y }, candidateBox) &&
        isPointInsideBox({ x: actualBox.x + actualBox.width, y: actualBox.y + actualBox.height }, candidateBox)) {
      score += 100; // High score for containment
    }

    // Calculate overlap area as a factor
    const overlap = calculateOverlap(actualBox, candidateBox);
    score += overlap;

    // Consider proximity if no containment
    if (score === 0) {
      const distance = Math.sqrt(
        Math.pow((actualBox.x + actualBox.width/2) - (candidateBox.x + candidateBox.width/2), 2) +
        Math.pow((actualBox.y + actualBox.height/2) - (candidateBox.y + candidateBox.height/2), 2)
      );
      // Convert distance to a score (closer = higher score)
      score = 1000 / (distance + 1);
    }

    if (score > bestScore) {
      bestScore = score;
      bestMatch = candidateInfo;
      highlighterLogger.debug('📏 New best spatial match:', {
        element: candidateInfo.element?.tagName,
        class: candidateInfo.element?.className,
        index: candidateInfo.index,
        score: score
      });
    }
  }

  if (bestMatch) {
    highlighterLogger.debug('✅ Final spatial match selected:', {
    element: bestMatch.element?.tagName,
    class: bestMatch.element?.className,
    index: bestMatch.index,
    finalScore: bestScore
  });
  return bestMatch;
}

return null;
}

/**
 * Finds a matching candidate element for an actual interaction element
 * 
 * @param candidate_elements Array of candidate element infos
 * @param actualInteractionElementInfo The actual interaction element info
 * @returns The matching candidate element info, or null if no match is found
 */
export function findMatchingCandidateElementInfo(candidate_elements, actualInteractionElementInfo) {
  if (!actualInteractionElementInfo.element || !actualInteractionElementInfo.bounding_box) {
    highlighterLogger.error('❌ Missing required properties in actualInteractionElementInfo');
    return null;
  }

  highlighterLogger.debug('🔍 Starting element matching for:', {
    clickedElement: actualInteractionElementInfo.element.tagName,
    clickedElementClass: actualInteractionElementInfo.element.className,
    totalCandidates: candidate_elements.length
  });

  // First try exact element match
  const exactMatch = findExactMatch(candidate_elements, actualInteractionElementInfo);
  if (exactMatch) {
    return exactMatch;
  }
  highlighterLogger.debug('❌ No exact element match found, trying parent matching...');

  // Try finding closest clickable parent
  const parentMatch = findParentMatch(candidate_elements, actualInteractionElementInfo);
  if (parentMatch) {
    return parentMatch;
  }
  highlighterLogger.debug('❌ No parent match found, falling back to spatial matching...');

  // If no exact or parent match, look for spatial relationships
  const spatialMatch = findSpatialMatch(candidate_elements, actualInteractionElementInfo);
  if (spatialMatch) {
    return spatialMatch;
  }

  highlighterLogger.error('❌ No matching element found for actual interaction element:', actualInteractionElementInfo);
  return null;
}
